import { useEffect, useRef, useState, useCallback } from "react";
import { Dna, Loader2, UploadCloud, Trash2, Sparkles, Clock, Tag, Type } from "lucide-react";
import { getStyleDNA, ingestStyleDNA, clearStyleDNA } from "../lib/api";
import { toast } from "sonner";

export default function StyleDNA() {
  const [state, setState] = useState({ status: "idle", ref_count: 0, dna: null });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { setState(await getStyleDNA()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (state.status === "processing") {
      pollRef.current = setInterval(load, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [state.status, load]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 30) { toast.error("Up to 30 clips at a time"); return; }
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      await ingestStyleDNA(fd);
      toast.success(`Analyzing ${files.length} clip${files.length === 1 ? "" : "s"} — building your Style DNA`);
      setState((s) => ({ ...s, status: "processing" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const reset = async () => {
    try { await clearStyleDNA(); setState({ status: "idle", ref_count: 0, dna: null }); toast.success("Style DNA cleared"); }
    catch { toast.error("Could not clear"); }
  };

  const dna = state.dna;
  const busy = state.status === "processing";

  return (
    <div className="border border-accent/30 bg-accent/[0.04] p-6" data-testid="style-dna-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center bg-accent/15 text-accent">
            <Dna className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-2xl tracking-wide text-white">CREATOR STYLE DNA</h3>
            <p className="mt-1 max-w-xl font-body text-sm text-muted-foreground">
              Upload 10–30 of your best-performing clips. Clip Miner learns the moments you actually post —
              types, pacing, length and tags — and uses it to pick and cut future clips like you would.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="video/*,audio/*" multiple hidden onChange={onFiles} data-testid="style-dna-input" />
          <button
            data-testid="style-dna-upload"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || busy}
            className="flex h-10 items-center gap-2 bg-accent px-4 text-xs font-semibold uppercase tracking-[0.15em] text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {uploading || busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {busy ? "Analyzing…" : dna ? "Add more clips" : "Upload best clips"}
          </button>
          {dna && !busy && (
            <button data-testid="style-dna-clear" onClick={reset}
                    className="grid h-10 w-10 place-items-center border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {busy && (
        <div className="mt-5 flex items-center gap-3 font-mono text-xs text-accent" data-testid="style-dna-processing">
          <Loader2 className="h-4 w-4 animate-spin" /> Transcribing & analyzing your clips… this runs in the background.
        </div>
      )}

      {state.status === "error" && (
        <p className="mt-5 font-mono text-xs text-primary">Something went wrong analyzing your clips. Try uploading again.</p>
      )}

      {dna && !busy && (
        <div className="mt-6 space-y-5" data-testid="style-dna-result">
          <p className="font-mono text-[10px] uppercase tracking-widest text-accent">
            Learned from {state.ref_count} clip{state.ref_count === 1 ? "" : "s"}
          </p>
          {dna.summary && <p className="font-body text-sm leading-relaxed text-white/90">{dna.summary}</p>}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat icon={Clock} label="Avg length" value={dna.avg_len_sec ? `${dna.avg_len_sec}s` : "—"} />
            <Stat icon={Type} label="Caption style" value={dna.caption_style || "—"} />
            <Stat icon={Tag} label="Tag style" value={dna.tag_style || "—"} />
          </div>
          {dna.preferred_moment_types?.length > 0 && (
            <Chips title="Moments you post" items={dna.preferred_moment_types} />
          )}
          {dna.catchphrases?.length > 0 && (
            <Chips title="Signature phrases" items={dna.catchphrases} accent />
          )}
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <Sparkles className="h-3 w-3 text-accent" /> Baked into how future shows are mined
          </p>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="border border-border bg-card p-3">
    <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
      <Icon className="h-3 w-3" /> {label}
    </p>
    <p className="mt-1 font-body text-sm text-white">{value}</p>
  </div>
);

const Chips = ({ title, items, accent }) => (
  <div>
    <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{title}</p>
    <div className="mt-2 flex flex-wrap gap-2">
      {items.map((t, i) => (
        <span key={i} className={`px-2.5 py-1 font-mono text-[11px] ${accent ? "bg-accent/15 text-accent" : "border border-border text-white/90"}`}>
          {t}
        </span>
      ))}
    </div>
  </div>
);
